import { ArrowRight, Plus, X } from "lucide-react";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";

function FAQItem({ question, answer, isOpen, onClick, iconColor }) {
  return (
    <div className="bg-white border-[0.5px] border-[#ddd] rounded-[20px] px-4 py-4 sm:px-6 sm:py-6 overflow-hidden flex flex-col gap-[25px]">
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={onClick}
      >
        <p className="font-semibold text-xl text-black enfinity-header-font">
          {question}
        </p>
        <div className="shrink-0 w-6 h-6 flex items-center justify-center">
          {isOpen ? (
            <X className="w-6 h-6 text-black" />
          ) : (
            <Plus className="w-6 h-6 text-black" />
          )}
        </div>
      </div>

      {isOpen && answer && (
        <p className="font-normal text-lg text-black opacity-50 leading-normal whitespace-pre-wrap enfinity-subtext-font">
          {answer}
        </p>
      )}
    </div>
  );
}

export default function FAQSection({
  faqs: customFaqs,
  buttonColor,
  iconColor,
}) {
  const defaultFaqs = [
    {
      question: "How do daily payouts work?",
      answer:
        "Instead of waiting for YouTube’s monthly payout, EnfiniPay lets you access your earnings sooner with flexible payment cycles. As soon as your videos generate revenue, the money is yours. No more 30+ day waits. Keep your cash flow steady and access your hard-earned money when you need it.",
    },
    {
      question: "What payment methods are available?",
      answer:
        "EnfiniPay supports withdrawals to bank accounts, debit or credit cards, e-wallets, PayPal, and Payoneer. Depending on your region, you may also withdraw in supported national currencies or crypto.",
    },
    {
      question: "Is my money secure with EnfiniPay?",
      answer:
        "EnfiniPay uses secure infrastructure and payment processing systems to safeguard transactions and account activity. We implement verification procedures and monitoring systems designed to protect your funds and account access.",
    },
    {
      question: "Can I switch between different payment methods?",
      answer:
        "Yes. You can update or switch your payout method through your account settings. Changes may require verification and may apply to future withdrawals depending on processing timelines.",
    },
  ];

  const faqs = customFaqs || defaultFaqs;

  const [openIndex, setOpenIndex] = useState(0);
  const location = useLocation();

  const handleToggle = (index) => {
    setOpenIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  // Get icon color based on route or prop
  const getIconColor = () => {
    if (iconColor) return iconColor;

    const path = location.pathname;

    if (path.includes("/our-solutions/enfinipay")) {
      return "#00A656";
    } else if (path.includes("/our-solutions/enfinishield")) {
      return "#8B5CF6";
    } else if (
      path === "/" ||
      path === "/enfinity" ||
      path === "/enfinity/main" ||
      path.includes("/enfinity/main")
    ) {
      // Main page: use primary button color (green from gradient)
      return "#00D115";
    } else {
      // Default: EnfiniBoost or other routes
      return "#3B82F6";
    }
  };

  // Get contact-us path based on route (for "Message us" button)
  const getContactUsPath = () => {
    const path = location.pathname;
    const landingPage = import.meta.env.VITE_LANDING_PAGE;
    if (
      path.includes("/our-solutions/enfinipay") ||
      (path === "/" && landingPage === "pay")
    ) {
      return "/our-solutions/enfinipay/contact-us";
    }
    if (
      path.includes("/our-solutions/enfiniboost") ||
      (path === "/" && landingPage === "boost")
    ) {
      return "/our-solutions/enfiniboost/contact-us";
    }
    if (
      path.includes("/our-solutions/enfinishield") ||
      (path === "/" && landingPage === "shield")
    ) {
      return "/our-solutions/enfinishield/contact-us";
    }
    return "/contact-us";
  };

  // Get button color based on route or prop
  const getButtonStyle = () => {
    if (buttonColor) {
      return { background: buttonColor };
    }

    const path = location.pathname;
    const landingPage = import.meta.env.VITE_LANDING_PAGE;

    if (
      path.includes("/our-solutions/enfinipay") ||
      (path === "/" && landingPage === "pay")
    ) {
      return { background: "linear-gradient(to right, #00A656, #127E00)" };
    } else if (
      path.includes("/our-solutions/enfinishield") ||
      (path === "/" && landingPage === "shield")
    ) {
      return { background: "#8B5CF6" };
    } else if (
      path.includes("/our-solutions/enfiniboost") ||
      (path === "/" && landingPage === "boost")
    ) {
      return { background: "linear-gradient(to right, #60A5FA, #3B82F6)" };
    } else {
      // Default: use new primary button color
      return { background: "#292929" };
    }
  };

  return (
    <div
      id="faqs"
      className="bg-white py-20 lg:py-32 px-6 xxs:px-2 xs:px-2 sm:px-8 md:px-12 5xl:max-w-[100rem] mx-auto"
    >
      <div className="flex flex-col items-center gap-8 max-w-5xl mx-auto px-4 sm:px-0">
        <p className="text-[#757575] text-lg 4xl:text-[24px] font-medium text-center">
          What do you have in mind?
        </p>

        <h2 className="text-5xl xxs:text-[55px] lg:text-[64px] 4xl:text-[90px] font-normal text-black text-center enfinity-header-font leading-normal enfinity-header-font">
          Frequently asked questions
        </h2>

        <div className="flex flex-col gap-[25px] items-stretch w-full max-w-[850px] mt-8 px-4 sm:px-0">
          {faqs.map((faq, idx) => (
            <FAQItem
              key={idx}
              question={faq.question}
              answer={faq.answer}
              isOpen={openIndex === idx}
              onClick={() => handleToggle(idx)}
              iconColor={getIconColor()}
            />
          ))}
        </div>

        <div className="mt-12 px-4 sm:px-0">
          <Link to={getContactUsPath()}>
            <button
              className="flex items-center justify-center gap-[10px] border-transparent px-6 py-3 rounded-full text-white font-medium text-[15px] hover:-translate-y-1 hover:shadow-xl transition-all duration-300 ease-out"
              style={getButtonStyle()}
            >
              <span>Still have questions? Message us</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
}
